import React from 'react'
import { ComposedChart, ResponsiveContainer } from 'recharts'

import { Icon } from '../../../components'
import { Area } from '../../../components/chart/Area'
import { CartesianGrid } from '../../../components/chart/CartesianGrid'
import { Label } from '../../../components/chart/Label'
import { Line } from '../../../components/chart/Line'
import { Tooltip, TooltipRow } from '../../../components/chart/Tooltip'
import { DateFormat, SOLID_AXIS_LINE, XAxis, dateAxisFormatter } from '../../../components/chart/XAxis'
import { YAxis } from '../../../components/chart/YAxis'
import { useFinancialsTableContext } from '../../../components/financials-table'
import { formatDollar, shortFormatDollar, shortFormatNumber } from '../../../util'
import { ESTIMATE_TYPE_LABEL } from '../../earnings-common/constants'
import { EarningsEstimateType, EarningsSettings } from '../../earnings-common/types'
import { getMonthlyTicks } from '../../earnings-common/utils'
import { PRICE_REVISIONS_CHART_HEIGHT, PRICE_REVISIONS_TICK_COUNT, PRICE_REVISIONS_YAXIS_WIDTH } from '../constants'
import { EarningRevision, RevisionsChartData } from '../types'
import { formatFiscalName } from '../utils'

enum DataKey {
  timestamp = 'timestamp',
  lowHigh = 'lowHigh',
  price = 'price',
  mean = 'mean',
  fiscalPeriod = 'fiscalPeriod',
  upRevisions = 'upRevisions',
  downRevisions = 'downRevisions',
}

export function formatEstimateLabel(estimateType: EarningsEstimateType, fiscalPeriod: string) {
  return `${formatFiscalName(fiscalPeriod)} ${ESTIMATE_TYPE_LABEL[estimateType]} Estimate`
}

function tooltipValueFormatter(value: number, { dataKey, payload }: { dataKey: string; payload?: EarningRevision }) {
  switch (dataKey) {
    case DataKey.fiscalPeriod:
      return `${shortFormatDollar(payload?.mean)} (${shortFormatDollar(payload?.low)}–${shortFormatDollar(payload?.high)})`
    case DataKey.price:
      if (typeof value !== 'number') return <span className="text-muted-2">—</span>
      return formatDollar(value)
    case DataKey.upRevisions:
      return `${value}/${payload?.estimates}`
    case DataKey.downRevisions:
      return `${value}/${payload?.estimates}`
  }
}

type Props = {
  chartData: RevisionsChartData
  endingTimestamp: number
  estimateType: EarningsEstimateType
  startingTimestamp: number
}

export function PriceRevisionsChart({ chartData, endingTimestamp, estimateType, startingTimestamp }: Props) {
  const { settings } = useFinancialsTableContext<EarningsSettings>()
  const monthlyTicks = React.useMemo(
    () => getMonthlyTicks(startingTimestamp, endingTimestamp),
    [startingTimestamp, endingTimestamp]
  )

  return (
    <ResponsiveContainer width="100%" height={PRICE_REVISIONS_CHART_HEIGHT}>
      <ComposedChart data={chartData} syncId="estimateRevisions" margin={{ left: 20, right: 24, top: 0, bottom: 0 }}>
        <CartesianGrid vertical />
        <XAxis
          axisLine={SOLID_AXIS_LINE}
          dataKey={DataKey.timestamp}
          domain={[startingTimestamp, endingTimestamp]}
          scale="time"
          tickFormatter={(value: number) =>
            dateAxisFormatter({ value, outFormat: DateFormat.MonthShort, breakOnYear: true })
          }
          ticks={monthlyTicks}
          type="number"
        />
        <YAxis
          domain={['auto', 'auto']}
          interval="preserveStartEnd"
          tickCount={PRICE_REVISIONS_TICK_COUNT}
          tickFormatter={(value) => shortFormatNumber(value)}
          width={PRICE_REVISIONS_YAXIS_WIDTH}
          yAxisId="estimate"
          orientation="right"
        >
          <Label dx={38} color="amber">
            {ESTIMATE_TYPE_LABEL[settings.estimateType]} Estimate
          </Label>
        </YAxis>
        <YAxis
          domain={['auto', 'auto']}
          interval="preserveStartEnd"
          tickCount={PRICE_REVISIONS_TICK_COUNT}
          width={PRICE_REVISIONS_YAXIS_WIDTH}
          yAxisId="price"
        >
          <Label dx={-36} color="blue">
            Close Price
          </Label>
        </YAxis>
        <Area connectNulls dataKey={DataKey.lowHigh} color="orangeLight" fillOpacity={0.12} yAxisId="estimate" />
        <Line dataKey={DataKey.price} yAxisId="price" />
        <Line dataKey={DataKey.mean} color="amber" yAxisId="estimate" />
        <Tooltip<number, EarningRevision>
          translucent
          formatLabel={(value) =>
            dateAxisFormatter({
              value,
              outFormat: DateFormat.MonthShortDayNumYearFull,
            })
          }
          formatValue={tooltipValueFormatter}
        >
          <TooltipRow<number, EarningRevision>
            isChartSeriesDataKey={false}
            indicator="amber"
            indicatorClass="flex justify-center"
            dataKey={DataKey.fiscalPeriod}
            label={(_, { payload }) => formatEstimateLabel(estimateType, payload?.fiscalPeriod ?? '')}
          />
          <TooltipRow
            indicator="blue"
            indicatorClass="flex justify-center"
            label="Close Price"
            dataKey={DataKey.price}
          />
          <TooltipRow
            isChartSeriesDataKey={false}
            indicator={<Icon width={12} name="arrowUpShort" className="-mb-0.5 text-green-500 dark:text-green-400" />}
            label="Up Revisions"
            dataKey={DataKey.upRevisions}
          />
          <TooltipRow
            isChartSeriesDataKey={false}
            indicator={<Icon width={12} name="arrowDownShort" className="-mb-0.5 text-red-500 dark:text-red-400" />}
            label="Down Revisions"
            dataKey={DataKey.downRevisions}
          />
        </Tooltip>
      </ComposedChart>
    </ResponsiveContainer>
  )
}
